import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import api from '../services/api';
import { PlusIcon, RefreshIcon, AlertIcon, CheckIcon, TrashIcon, EditIcon } from '../components/Icons';

const Templates = () => {
  const { t } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [connections, setConnections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logs, setLogs] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    language: 'pt_BR',
    category: 'UTILITY',
    headerType: 'none',
    headerText: '',
    bodyText: '',
    footerText: '',
    buttonType: 'none',
    buttons: []
  });

  const categories = [
    { value: 'UTILITY', label: 'Utilitario' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'AUTHENTICATION', label: 'Autenticacao' }
  ];

  const languages = [
    { value: 'pt_BR', label: 'Portugues (Brasil)' },
    { value: 'en_US', label: 'Ingles (EUA)' },
    { value: 'es', label: 'Espanhol' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const addLog = (type, text, details = null) => {
    const newLog = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString('pt-BR'),
      type,
      text,
      details
    };
    setLogs(prev => [newLog, ...prev].slice(0, 50));
  };

  const loadData = async () => {
    setLoadingTemplates(true);
    try {
      addLog('info', t('templates.a1'));
      const [templatesRes, connectionsRes] = await Promise.all([
        api.get('/templates'),
        api.get('/connections')
      ]);
      setTemplates(templatesRes.data);
      setConnections(connectionsRes.data.filter(c => c.status === 'connected'));

      addLog('success', `${templatesRes.data.length} templates carregados`);
      addLog('success', t('templates.a2', { v1: connectionsRes.data.filter(c => c.status === 'connected').length }));
    } catch (error) {
      console.error('Error loading data:', error);
      addLog('error', t('templates.a3'), error.message);
    }
    setLoadingTemplates(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Montar os components do template
    const components = [];

    // Header
    if (formData.headerType === 'text' && formData.headerText) {
      components.push({
        type: 'HEADER',
        format: 'TEXT',
        text: formData.headerText
      });
    }

    // Body (obrigatorio)
    if (formData.bodyText) {
      const bodyComponent = {
        type: 'BODY',
        text: formData.bodyText
      };

      // Detectar variaveis no body ({{1}}, {{2}}, etc)
      const matches = formData.bodyText.match(/\{\{\d+\}\}/g);
      if (matches) {
        bodyComponent.example = {
          body_text: [matches.map((_, i) => `exemplo${i + 1}`)]
        };
      }

      components.push(bodyComponent);
    }

    // Footer
    if (formData.footerText) {
      components.push({
        type: 'FOOTER',
        text: formData.footerText
      });
    }

    // Buttons
    if (formData.buttonType === 'quick_reply' && formData.buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: formData.buttons.map(btn => ({
          type: 'QUICK_REPLY',
          text: btn
        }))
      });
    }

    const payload = {
      name: formData.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      language: formData.language,
      category: formData.category,
      components
    };

    addLog('info', '========== CRIANDO TEMPLATE ==========');
    addLog('info', t('templates.a4', { v1: payload.name }));
    addLog('info', t('templates.a5', { v1: payload.language }));
    addLog('info', `Categoria: ${payload.category}`);
    addLog('info', '---------------------------------------');
    addLog('info', t('templates.a6'));

    try {
      const response = await api.post('/templates/create', payload);

      if (response.data.metaRequest) {
        addLog('request', t('templates.a7'), JSON.stringify(response.data.metaRequest, null, 2));
      }

      addLog('info', '---------------------------------------');
      addLog('success', t('templates.a8'));
      addLog('success', `Template ID: ${response.data.templateId}`);
      addLog('success', t('templates.a9', { v1: response.data.status }));
      addLog('info', '---------------------------------------');

      if (response.data.metaResponse) {
        addLog('success', t('templates.a10'), JSON.stringify(response.data.metaResponse, null, 2));
      }

      addLog('info', '=======================================');

      setMessage({
        type: 'success',
        text: t('templates.a11', { v1: response.data.status })
      });

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('templates.a12');

      if (error.response?.data?.metaRequest) {
        addLog('request', t('templates.a13'), JSON.stringify(error.response.data.metaRequest, null, 2));
      }

      addLog('info', '---------------------------------------');
      addLog('error', t('templates.a14'));
      addLog('error', t('templates.a15', { v1: errorMsg }));
      addLog('info', '---------------------------------------');

      if (error.response?.data?.metaError) {
        addLog('error', t('templates.a16'), JSON.stringify(error.response.data.metaError, null, 2));
      }

      addLog('info', '=======================================');

      setMessage({
        type: 'error',
        text: errorMsg
      });
    }

    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      language: 'pt_BR',
      category: 'UTILITY',
      headerType: 'none',
      headerText: '',
      bodyText: '',
      footerText: '',
      buttonType: 'none',
      buttons: []
    });
  };

  const addButton = () => {
    if (formData.buttons.length < 3) {
      setFormData(prev => ({
        ...prev,
        buttons: [...prev.buttons, '']
      }));
    }
  };

  const updateButton = (index, value) => {
    const newButtons = [...formData.buttons];
    newButtons[index] = value;
    setFormData(prev => ({ ...prev, buttons: newButtons }));
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-700';
      case 'PENDING': return 'bg-yellow-100 text-yellow-700';
      case 'REJECTED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'APPROVED': return 'Aprovado';
      case 'PENDING': return t('templates.a17');
      case 'REJECTED': return 'Rejeitado';
      default: return status;
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setDeleting(true);
    addLog('info', '========== DELETANDO TEMPLATE ==========');
    addLog('info', t('templates.a18', { v1: templateToDelete.name }));
    addLog('info', t('templates.a19'));

    try {
      const response = await api.delete(`/templates/${templateToDelete.name}`);

      addLog('info', '---------------------------------------');
      addLog('success', t('templates.a20'));
      addLog('success', response.data.message);
      addLog('info', '=======================================');

      setMessage({
        type: 'success',
        text: t('templates.a21', { v1: templateToDelete.name })
      });

      setShowDeleteModal(false);
      setTemplateToDelete(null);
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('templates.a22');

      addLog('info', '---------------------------------------');
      addLog('error', t('templates.a23'));
      addLog('error', t('templates.a24', { v1: errorMsg }));

      if (error.response?.data?.metaError) {
        addLog('error', t('templates.a25'), JSON.stringify(error.response.data.metaError, null, 2));
      }

      addLog('info', '=======================================');

      setMessage({
        type: 'error',
        text: errorMsg
      });
    }

    setDeleting(false);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('tpl.title')}</h1>
          <p className="text-gray-500 mt-1">{t('tpl.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshIcon className="w-5 h-5" />
            <span className="text-sm font-medium">{t('wa.refresh')}</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            disabled={connections.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-whatsapp text-white font-semibold rounded-lg hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('tpl.new')}</span>
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {connections.length === 0 && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-50 text-yellow-700 border border-yellow-200 flex items-start gap-3">
          <AlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{t('tpl.noActiveConnection')}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Lista de Templates */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">{t('tpl.existing')}</h2>
            </div>

            {loadingTemplates ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-whatsapp mx-auto"></div>
                <p className="text-gray-500 mt-4">{t('tpl.loading')}</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">{t('tpl.empty')}</p>
                <p className="text-gray-400 text-sm mt-1">{t('tpl.emptyHint')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {templates.map((template) => (
                  <div key={template.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-medium text-gray-900">{template.name}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(template.status)}`}>
                            {getStatusLabel(template.status)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>{template.language}</span>
                          <span>{template.category}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteClick(template)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('tpl.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Painel de Logs */}
        <div className="lg:col-span-2">
          <div className="bg-gray-900 rounded-2xl shadow-sm border border-gray-800 h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>{t('templates.j1')}</h3>
              <button
                onClick={clearLogs}
                className="text-xs text-gray-400 hover:text-white transition-colors"
              >
                Limpar
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[700px] font-mono text-xs">
              {logs.length === 0 ? (
                <div className="text-gray-500 text-center py-8">{t('templates.j2')}<br />{t('templates.j3')}</div>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className={`${log.type === 'request' ? 'flex flex-col gap-1' : 'flex gap-2'}`}>
                    <div className="flex gap-2">
                      <span className="text-gray-500 flex-shrink-0">[{log.timestamp}]</span>
                      <span className={`${
                        log.type === 'success' ? 'text-green-400' :
                        log.type === 'error' ? 'text-red-400' :
                        log.type === 'warning' ? 'text-yellow-400' :
                        log.type === 'request' ? 'text-purple-400' :
                        'text-blue-400'
                      }`}>
                        {log.type === 'success' && '+ '}
                        {log.type === 'error' && 'x '}
                        {log.type === 'warning' && '! '}
                        {log.type === 'info' && '> '}
                        {log.type === 'request' && '^ '}
                        {log.text}
                      </span>
                    </div>
                    {log.details && (
                      <pre className="ml-4 mt-1 p-2 bg-gray-800 rounded text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">
                        {log.details}
                      </pre>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="px-4 py-2 border-t border-gray-800 text-xs text-gray-500">
              {logs.length} {logs.length === 1 ? 'entrada' : 'entradas'}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Criar Template */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">{t('tpl.create')}</h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Nome do Template */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('templates.j4')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('tpl.namePlaceholder')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none transition-all"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('tpl.nameHint')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Idioma */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{t('templates.j5')}</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer transition-all"
                  >
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>{lang.label}</option>
                    ))}
                  </select>
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Categoria *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer transition-all"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Header */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Cabecalho (Header)
                </label>
                <select
                  value={formData.headerType}
                  onChange={(e) => setFormData({ ...formData, headerType: e.target.value, headerText: '' })}
                  className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer transition-all mb-2"
                >
                  <option value="none">{t('tpl.noHeader')}</option>
                  <option value="text">{t('tpl.text')}</option>
                </select>
                {formData.headerType === 'text' && (
                  <input
                    type="text"
                    value={formData.headerText}
                    onChange={(e) => setFormData({ ...formData, headerText: e.target.value })}
                    placeholder={t('tpl.headerText')}
                    className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none transition-all"
                  />
                )}
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('templates.j6')}</label>
                <textarea
                  value={formData.bodyText}
                  onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                  placeholder={t('tpl.bodyExample')}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none transition-all resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">{t('tpl.varsHintFull')}</p>
              </div>

              {/* Footer */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Rodape (Footer)
                </label>
                <input
                  type="text"
                  value={formData.footerText}
                  onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                  placeholder={t('tpl.footerText')}
                  className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none transition-all"
                />
              </div>

              {/* Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Botoes
                </label>
                <select
                  value={formData.buttonType}
                  onChange={(e) => setFormData({ ...formData, buttonType: e.target.value, buttons: [] })}
                  className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer transition-all mb-2"
                >
                  <option value="none">{t('tpl.noButtons')}</option>
                  <option value="quick_reply">{t('tpl.quickReply')}</option>
                </select>

                {formData.buttonType === 'quick_reply' && (
                  <div className="space-y-2">
                    {formData.buttons.map((btn, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={btn}
                          onChange={(e) => updateButton(index, e.target.value)}
                          placeholder={`Botao ${index + 1}`}
                          className="flex-1 h-10 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => removeButton(index)}
                          className="px-3 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    {formData.buttons.length < 3 && (
                      <button
                        type="button"
                        onClick={addButton}
                        className="flex items-center gap-2 text-sm text-whatsapp hover:text-whatsapp-dark"
                      >
                        <PlusIcon className="w-4 h-4" />{t('templates.j7')}</button>
                    )}
                    <p className="text-xs text-gray-500">{t('tpl.maxButtons')}</p>
                  </div>
                )}
              </div>

              {/* Botoes de Acao */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                >{t('templates.j8')}</button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Criando...' : 'Criar Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal de Confirmacao de Exclusao */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
                Deletar Template
              </h2>
              <p className="text-gray-600 text-center mb-4">
                Tem certeza que deseja deletar o template <strong>"{templateToDelete.name}"</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <p className="font-medium mb-1">{t('tpl.warning')}</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>{t('tpl.irreversible')}</li>
                      <li>{t('tpl.removedFromMeta')}</li>
                      <li>{t('tpl.nameLocked')}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setTemplateToDelete(null); }}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                >{t('templates.j9')}</button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deletando...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="w-5 h-5" />
                      Deletar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
